import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../client';
import { queryKeys } from './keys';
import type { PromptInput, PromptVersionDetail, PromptVersionList } from '../types';

export function usePromptVersions() {
  return useQuery({
    queryKey: queryKeys.promptVersions,
    queryFn: () => api<PromptVersionList>('/api/business-opportunities/prompt-versions'),
  });
}

export function usePromptVersion(version: number | null) {
  return useQuery({
    queryKey: queryKeys.promptVersion(version ?? 0),
    queryFn: () => api<PromptVersionDetail>(`/api/business-opportunities/prompt-versions/${version}`),
    enabled: version !== null,
  });
}

/** Where the Impact × Feasibility chart splits both axes. */
export function useMatrix() {
  return useQuery({
    queryKey: queryKeys.matrix,
    queryFn: () => api<{ threshold: number }>('/api/business-opportunities/matrix'),
  });
}

export function useSaveMatrix() {
  const client = useQueryClient();
  return useMutation({
    meta: { success: 'Priority axes saved' },
    mutationFn: (body: { threshold: number }) => api<{ threshold: number }>('/api/business-opportunities/matrix', 'PUT', body),
    onSuccess: (data) => {
      client.setQueryData(queryKeys.matrix, data);
      // The catalogue carries the split to both charts.
      void client.invalidateQueries({ queryKey: queryKeys.ideaCatalogue });
      void client.invalidateQueries({ queryKey: queryKeys.dimensions });
    },
  });
}

/** Saves the prompt as a new version (or returns the current one when nothing changed). */
export function useSavePrompt() {
  const client = useQueryClient();
  return useMutation({
    meta: { success: (version: PromptVersionDetail) => `Prompt saved as v${version.version}` },
    mutationFn: (body: PromptInput) => api<PromptVersionDetail>('/api/business-opportunities/prompt', 'PUT', body),
    onSuccess: (data) => {
      client.setQueryData(queryKeys.promptVersion(data.version), data);
      void client.invalidateQueries({ queryKey: queryKeys.promptVersions });
      void client.invalidateQueries({ queryKey: queryKeys.processes });
    },
  });
}
